import React, {
  useEffect,
  forwardRef,
  useRef,
  useState,
  useImperativeHandle,
} from "react";
import { Dropdown } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "@bhunjadi/react-select-v3";
import { useTenantConfig } from "src/main_app/actions/tenant";
import { isNilOrEmpty, addDays } from "src/main_app/utils/common";
import dayjs from "dayjs";

import "./nrw-filter.style.scss";

const filterWithDefault = {
  zone: false,
  dateRange: false,
};

const zoneOptions = [{ id: "1", label: "CameronPark1WPS" }];

const NonRevenueWaterFilter = ({ onFiltersApply, disabled }, ref) => {
  const tenantConfig = useTenantConfig();
  const toggleRef = useRef(null);
  const maxDateRef = useRef(addDays(new Date(), -1));
  const [filterWith, setFilterWith] = useState({
    ...filterWithDefault,
    dateRange: true, // ✅ enable date range by default
  });

  const [startDate, setStartDate] = useState(
    dayjs().subtract(7, "day").toDate()
  ); // ✅ 7 days ago
  const [endDate, setEndDate] = useState(dayjs().subtract(1, "day").toDate());
  const [zoneFilter, setZoneFilter] = useState();
  const [filterCount, setFilterCount] = useState(0);

  const toggleClick = () => toggleRef.current?.click();

  const onApply = () => {
    const filters = {
      startDate,
      endDate,
      zone: zoneFilter,
    };
    onFiltersApply(filters);
  };

  const onApplyClick = () => {
    onApply();
    setTimeout(toggleClick, 200);
  };

  const filtersClear = () => {
    toggleClick();

    // Set date range back to last 7 days
    const newStart = dayjs().subtract(7, "day").toDate();
    const newEnd = dayjs().subtract(1, "day").toDate();

    setStartDate(newStart);
    setEndDate(newEnd);
    setZoneFilter(null);

    // Keep dateRange checked, reset others
    setFilterWith({
      ...filterWithDefault,
      dateRange: true,
    });

    // Recalculate filter count (1 active filter: dateRange)
    setFilterCount(1);

    // Trigger loadData with default 2-day filters
    onFiltersApply({
      startDate: newStart,
      endDate: newEnd,
      zone: null,
    });
  };

  // 🔹 Update filter count whenever filters change
  useEffect(() => {
    let count = 0;
    if (filterWith.dateRange && startDate && endDate) count += 1;
    if (filterWith.zone && zoneFilter && zoneFilter !== "All") count += 1;
    setFilterCount(count);
  }, [filterWith, startDate, endDate, zoneFilter]);

  // 🔹 Expose functions to parent (two-way control)
  useImperativeHandle(ref, () => ({
    getFilters: () => ({
      startDate,
      endDate,
      zone: zoneFilter,
      count: filterCount,
    }),
    clearFilters: filtersClear,
    setFilters: (filters) => {
      if (!filters) return;

      if (filters.startDate || filters.endDate)
        setFilterWith((prev) => ({ ...prev, dateRange: true }));
      if (filters.zone) setFilterWith((prev) => ({ ...prev, zone: true }));

      setStartDate(filters.startDate || null);
      setEndDate(filters.endDate || null);
      setZoneFilter(filters.zone || null);

      // 🔹 Update count immediately
      let count = 0;
      if (filters.startDate && filters.endDate) count += 1;
      if (filters.zone && filters.zone !== "All") count += 1;
      setFilterCount(count);
    },
  }));

  useEffect(() => {
    if (isNilOrEmpty(tenantConfig)) return;
  }, [tenantConfig]);

  return (
    <div className="filter-button filter alert--filter-button">
      <Dropdown>
        <Dropdown.Toggle
          variant=""
          className="filter-button__btn p-0"
          ref={toggleRef}
          disabled={disabled}
        >
          <div className="btn-group">
            <span className="btn btn-default">
              <i className="far fa-filter mr-2" /> Filter
            </span>
            {filterCount > 0 && (
              <span className="btn btn-default filter-count">
                {filterCount}
              </span>
            )}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <div className="filter__header block-center-between">
            <button
              className="filter__clear btn btn-default"
              onClick={filtersClear}
            >
              Clear
            </button>
            <span className="filter__label">Filters</span>
            <button
              className="filter__apply btn btn-default"
              onClick={onApplyClick}
            >
              Apply
            </button>
          </div>

          <div className="filter__body">
            <div className="filter__options f-14">
              {/* Date Range */}
              <div className="filter__option">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={filterWith.dateRange}
                    onChange={() =>
                      setFilterWith((prev) => ({
                        ...prev,
                        dateRange: !prev.dateRange,
                      }))
                    }
                  />
                  <span className="ck" />
                  Date range
                </label>
              </div>

              {filterWith.dateRange && (
                <div className="filter__footer">
                  <div>from:</div>
                  <div className="month-range-picker start-date">
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={maxDateRef.current}
                      dateFormat="dd MMM yyyy"
                    />
                  </div>
                  <div>to:</div>
                  <div className="month-range-picker end-date">
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      dateFormat="dd MMM yyyy"
                    />
                  </div>
                </div>
              )}

              {/* Zone */}
              <div className="filter__option">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={filterWith.zone}
                    onChange={() =>
                      setFilterWith((prev) => ({
                        ...prev,
                        zone: !prev.zone,
                      }))
                    }
                  />
                  <span className="ck" />
                  Zone
                </label>
              </div>

              {filterWith.zone && (
                <div className="filter__footer">
                  <Select
                    defaultValue={{ id: "-All-", label: "All" }}
                    onChange={(e) => setZoneFilter(e.label)}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.id}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    options={zoneOptions}
                  />
                </div>
              )}
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default forwardRef(NonRevenueWaterFilter);
